import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@material-ui/core'
import { Error } from '@material-ui/icons'
import useInput from 'src/hooks/useInput'
import emailSentImg from 'src/assets/images/password/emailSent.svg'
import { sendForgotPasswordEmail } from 'src/services/api/EmailsApi/PasswordReset/forgotPassword'
import BreadcrumbsComponent from 'src/components/BreadcrumbsComponent/BreadcrumbsComponent'
import { useGlobalStyles } from 'src/mui/styles/globalStyles'
import { useStyles } from './ForgotPasswordContent.styles'

const ForgotPasswordContent = () => {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const [emailSent, setEmailSent] = useState(false)
  const email = useInput('')
  const [isEmpty, setIsEmpty] = useState(false)
  const [isEmailInvalid, setIsEmailInvalid] = useState(false)

  const classesGlobal = useGlobalStyles()
  const classes = useStyles()

  const checkIsEmail = (email: string) => {
    if (
      email
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      return true
    }
    setIsEmailInvalid(true)
    return false
  }

  const handleEmail = async (email: string) => {
    setLoading(true)
    if (email.length === 0) {
      setIsEmpty(true)
    }
    try {
      if (email.length > 0 && checkIsEmail(email)) {
        await sendForgotPasswordEmail({ email: email })
        setEmailSent(true)
      }
    } catch (e) {
      setIsEmailInvalid(true)
    }
    setLoading(false)
  }

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleEmail(email.value).then()
    }
  }

  const tryAgainSendEmail = () => {
    setLoading(false)
    setEmailSent(false)
    email.setValue('')
  }

  useEffect(() => {
    setIsEmpty(false)
    setIsEmailInvalid(false)
  }, [email.value])

  return (
    <Box className={classesGlobal.containerPages}>
      {!emailSent ? (
        <>
          <Box>
            <BreadcrumbsComponent
              crumbs={[
                { label: t('navbar.start'), path: '/' },
                { label: t('login-modal.forgot-password') }
              ]}
            />
            <Typography className={classesGlobal.titleSection}>
              {t('password.forgot')}
            </Typography>
            <Typography variant="h5" style={{ fontWeight: 400 }}>
              {t('password.dont-worry')}
            </Typography>
            <Box>
              <Box className={classes.emailSection}>
                <Typography
                  variant="h5"
                  component="label"
                  htmlFor="resert-email-lsbd"
                  className={classes.inputLabel}
                >
                  {t('password.insert-email')}
                </Typography>
                <TextField
                  variant="outlined"
                  className={classes.input}
                  onChange={email.onChange}
                  type="email"
                  placeholder={t('password.input-email')}
                  label={null}
                  id="resert-email-lsbd"
                  error={isEmpty || isEmailInvalid}
                  onKeyDown={handleKey}
                />
              </Box>
              {isEmpty && (
                <Box className={classes.invalidInput}>
                  <Error color="error" />
                  <Typography variant="body1">
                    {t('form.field-required')}
                  </Typography>
                </Box>
              )}
              {isEmailInvalid && !isEmpty && (
                <Box className={classes.invalidInput}>
                  <Error color="error" />
                  <Typography variant="body1">
                    {t('password.email-invalid')}
                  </Typography>
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              onClick={() => handleEmail(email.value)}
              className={classes.submitButton}
              disabled={loading}
            >
              {!loading ? (
                t('password.request')
              ) : (
                <CircularProgress size={16} />
              )}
            </Button>
          </Box>
        </>
      ) : (
        <Box>
          <BreadcrumbsComponent
            crumbs={[
              { label: t('navbar.start'), path: '/' },
              {
                label: t('login-modal.forgot-password')
              },
              { label: t('password.reset-email') }
            ]}
          />
          <Box className={classes.containerSendEmail}>
            <Box>
              <img src={emailSentImg} alt="email icon" />
            </Box>
            <Box className={classes.containerSendEmailInfo}>
              <Typography variant="h3" className={classes.titleSection}>
                {t('password.sent-email')}
              </Typography>
              <Typography
                variant="h5"
                className={classes.sendEmailInfoSubtitle}
              >
                {t('password.email-instructions')}
              </Typography>
            </Box>
            <Box className={classes.containerSendEmailAgain}>
              <Typography variant="body1">
                {t('password.not-receive')}
              </Typography>
              <Button
                variant="text"
                onClick={() => tryAgainSendEmail()}
                className={classes.sendAgainButton}
                disableElevation
                disableRipple
              >
                <Typography variant="body1">
                  {t('password.click-here')}
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ForgotPasswordContent
